import { Proto } from "../../pojo/Proto";
import {
    GridDataResponse,
    GridDataTerm,
    GridEvent,
    GridItemChange,
    GridListItemQuery,
    GridOrganization,
    GridReservation,
    GridSpace,
    GridsService,
} from "../../services/grids.service";
import { DowGrid } from "../s25-virtual-grid/s25.dow.grid.component";
import { Grid } from "../s25-virtual-grid/s25.virtual.grid";
import { computed, signal, Signal, untracked, WritableSignal } from "@angular/core";
import { AvailService, EventPerm } from "../../services/avail.service";
import { Flavor, LooseAutocomplete, matchKeyAndProp, Merge, Signals } from "../../pojo/Util";
import { DropDownItem } from "../../pojo/DropDownItem";
import type { Conflict } from "../s25-conflicts/s25.conflicts.component";
import { RecordType } from "../s25-event/ProfileI";
import { S25Util } from "../../util/s25-util";
import { BoardUtil } from "../s25-swarm-schedule/s25.board.util";
import { EventSummary, EventSummaryService } from "../s25-swarm-schedule/s25.event.summary.service";
import { hasEditFls } from "../../pojo/Fls";
import { ProfileUtil } from "../s25-datepattern/profile.util";
import { S25Const } from "../../util/s25-const";
import { BOARD_CONST } from "../s25-swarm-schedule/s25.board.const";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { Extractable, GenericDropdownUtil } from "../s25-dropdown/generic/generic.dropdown.util";
import { S25Assert } from "../../util/s25.assert";
import { Event } from "../../pojo/Event";
import { EventI } from "../../pojo/EventI";
import { MPGUtil } from "../s25-meeting-pattern-grid/s25.meeting.pattern.grid.util";
import { Relationships } from "../../services/space.service";
import { AcademicGridAvailability } from "./s25.academic.grid.conflicts";
import { isDefined } from "../../util/core";

export namespace AcademicGrid {
    import Unwrap = Proto.Unwrap;
    import ISODateString = Proto.ISODateString;
    import ISOTimeString = Proto.ISOTimeString;

    export type Position = {
        dow: string;
        startHour: number;
        endHour: number;
        locations: number[];
        instructors: number[];
    };

    export type Data = { rows: Row[]; items: SourceItem[]; message?: string };
    export type Row = Grid.SourceHeader & { academicGrid: RowData };
    export type SourceItem = DowGrid.SourceItem<Position> & { academicGrid: ItemData };
    export type Item = DowGrid.Item<SourceItem>;
    export type ProjectionData = DowGrid.ProjectionData & {
        locationId?: number | "none";
        instructorId?: number | "none";
    };
    export type GrabbedEntry = {
        rowKey: string;
        entry: DowGrid.PickupContext<Item, ProjectionData>["grabbedEntry"];
    };

    /** instructorId → single DOW character → items sorted by committed startHour */
    export type InstructorTimeIndex = Map<number, Map<string, Item[]>>;

    export type SaveType = "individual" | "bulk";
    export type Mode = "schedule" | "instructor" | "location";

    export type RowData = {
        hidden: WritableSignal<boolean>;
        schedule?: EventTooltipData & { id: number; header: string; profileId: number };
        instructor?: { id: number | "none"; name: string };
        location?: { id: number | "none"; name: string };
    };
    export type ItemData = EventTooltipData & {
        initialized: WritableSignal<Item | undefined>;
        profileId: number;
        profileAlienUid: string;
        boundProfiles: number[];
        adHoc: boolean;
        issues: {
            hasIssues: Signal<boolean>;
            instructorConflicts: Signal<boolean>;
            locationConflicts: WritableSignal<boolean>;
            resourceConflicts: WritableSignal<boolean>;
            locationUndersized: Signal<boolean>;
            locationOversized: Signal<boolean>;
            standardSchedules: Signal<boolean>;
            missingFeaturePrefs: Signal<boolean>;
            missingPartitionPrefs: Signal<boolean>;
        };
        setup: number; // Minutes
        pre: number; // Minutes
        post: number; // Minutes
        takedown: number; // Minutes
        setupPreHours: number;
        postTakedownHours: number;
        dragMessage?: string;
        isMakeupDate: boolean;
        isConversionDate: boolean;
        isExamDate: boolean;
        profileCount: number; // Number of profiles on event
        rowKey: Signal<string>; // The key of the row that the item is in
        displayAsConflict: Signal<boolean>;
        noShadows: Signal<boolean>;
        // 1. Original — last saved snapshot
        // 2. Current — where the item is on the grid (lives on the tooltip data)
        // 3. Candidate — live drag / overrides
        // 4. Effective — candidate with fallback to current
        original: WritableSignal<CandidateData>;
        candidate: { position: { [K in keyof Position]: Signal<Position[K] | undefined> } } & Signals<DerivedData>;
        effective: CandidateSignals;
        linkedItems: Item[];
        removedOrphanedAlsoAssignLocations: WritableSignal<Set<number>>;
        swappedLocations: WritableSignal<{ old: number; new: number } | undefined>;
        filteredOut: WritableSignal<boolean>;
        candidateAlsoAssign: Signal<number[]>;
        changedLocation: Signal<{ new: number | "none"; old: number | "none" } | undefined>;
    };

    export type CandidateData = { position: Position } & DerivedData;

    export type CandidateSignals = { position: Signals<Position> } & Signals<DerivedData>;

    export type DerivedData = {
        eventStartHour: number;
        eventEndHour: number;
        occurrences: Occurrence[];
        profileCode: string;
        profileName: string;
        startDate: ISODateString;
        endDate: ISODateString;
        locations: NamedEntity[];
        instructors: NamedEntity[];
    };

    export type NamedEntity = { id: number; name: string | undefined };

    export type Occurrence = Merge<
        GridReservation,
        {
            locations: (GridReservation["locations"][number] & { ap: EventPerm })[];
            resources: (GridReservation["resources"][number] & { ap: EventPerm })[];
        }
    >;

    export type EventTooltipData = {
        current: WritableSignal<CandidateData>;
        alienUid: string;
        eventId: number;
        eventName: string;
        eventTitle: string;
        reference: string;
        expectedHeadCount: number;
        registeredHeadCount: number;
        isLynx: boolean;
        featurePreferences: { id: number; name: string }[];
        partitionPreferences: { id: number; name: string }[];
        subjectCode?: string;
        courseReferenceNumber?: string;
        term?: GridDataTerm;
        subTerm?: string;
        sectionType?: string;
        primaryOrganization?: GridOrganization;
        isLocationConflict: boolean; // Read-only "other reservation" block, not part of this grid
        setup: number; // Minutes
        pre: number; // Minutes
        post: number; // Minutes
        takedown: number; // Minutes
    };

    export type EventData = NonNullable<Unwrap<Awaited<ReturnType<typeof GridsService.getData>>>>["events"][number];

    export type TimeSet = {
        id: string;
        dow: string;
        startHour: number;
        endHour: number;
        profile: EventData["profiles"][number];
        event: EventData;
        tooltipData: EventTooltipData;
    };

    export type Filter<ItemType extends "item" | "row" = "item"> = {
        id?: string;
        label: string;
        type: "text" | "numberRange" | "contact" | "dropdownMultiselect" | "yesNo" | "options" | "none";
        value: WritableSignal<any>;
        active: Signal<boolean>;
        showText?: Signal<boolean>;
        clear: () => void;
        text: Signal<string>;
        filter: (row: ItemType extends "item" ? Item : Row) => boolean;
        data?: MultiselectFilterData;
        keepRow?: boolean;
    };

    export type MultiselectFilterData = {
        items: DropDownItem[];
        placeholder: string;
    };

    export type Issues = {
        counts: { types: number; total: number; warningLevels: number } & Record<WarningLevel, number>;
        types: IssueType[];
    };

    export type IssueTypeId = keyof typeof allIssueTypes;
    export type ItemIssueType = keyof typeof itemIssueTypes;

    export type IssueType = {
        id: LooseAutocomplete<IssueTypeId>;
        label: string;
        action: WarningLevel;
        issues: Issue[];
        // Default = true
        showCount?: boolean;
    };

    export type Issue = {
        item: Item;
        items?: Item[];
        time: string;
        message?: string;
        messages?: string[];
        load?: () => Promise<Conflict[]>;
        location?: GridSpace;
        headCountLabel?: string;
        headCount?: number;
        missingPrefs?: string[];
        relationshipType?: Relationships.TypeId;
        locations?: number[];
        resources?: number[];
        events?: number[];
    };

    export type WarningLevel = keyof typeof warningSeverities;
    export type WarningConfigItem = { action: WarningLevel; maxSeverity?: WarningLevel };
    export type WarningHeadCountRatio = "EHC:CAP" | "RHC:CAP";
    export type WarningsConfig = Merge<
        Record<ItemIssueType, WarningConfigItem>,
        {
            locationUndersized: WarningConfigItem & {
                ratio: WarningHeadCountRatio;
                threshold: Flavor<number, "[0-100]">;
            };
            locationOversized: WarningConfigItem & {
                ratio: WarningHeadCountRatio;
                threshold: Flavor<number, "[0-100]">;
            };
        }
    >;

    /** Top (percent) that parks a tile below the grid when its row is not part of the current view */
    export const offGridTop = 101;

    const matchId = matchKeyAndProp("id");
    export const warningSeverities = matchId({
        ignore: { id: "ignore", label: "Ignore", severity: 0 },
        info: { id: "info", label: "Highlight", severity: 1 },
        warn: { id: "warn", label: "Warn", severity: 2 },
        prevent: { id: "prevent", label: "Prevent", severity: 3 },
    } as const);
    export const itemIssueTypes = matchId({
        instructorConflicts: { id: "instructorConflicts", label: "Instructor Conflict" },
        locationConflicts: { id: "locationConflicts", label: "Location Conflict" },
        resourceConflicts: { id: "resourceConflicts", label: "Resource Conflict" },
        locationUndersized: { id: "locationUndersized", label: "Undersized Location" },
        locationOversized: { id: "locationOversized", label: "Oversized Location" },
        standardSchedules: { id: "standardSchedules", label: "Standard Schedule Conflict" },
        missingFeaturePrefs: { id: "missingFeaturePrefs", label: "Missing Feature" },
        missingPartitionPrefs: { id: "missingPartitionPrefs", label: "Missing Partition" },
    } as const);
    export const saveIssueTypes = matchId({
        saveMessages: { id: "saveMessages", label: "Warning" },
        saveErrors: { id: "saveErrors", label: "Error" },
    });
    const allIssueTypes = { ...itemIssueTypes, ...saveIssueTypes };

    export function getTimeSets(data: GridDataResponse, events: GridEvent[]): TimeSet[] {
        const changeByProfileId = new Map(data.changes.map((change) => [change.profileId, change]));
        return events
            .filter((event) => !!event.profiles?.length)
            .flatMap((event) =>
                event.profiles
                    .filter((profile) => {
                        return profile.recType === RecordType.RecurrenceGrammar || !!profile.reservations?.length;
                    })
                    .flatMap((profile) => {
                        const change = changeByProfileId.get(profile.id);
                        const profileStartHour = S25Util.date.dateToHours(change?.startTime ?? profile.start);
                        const profileEndHour = S25Util.date.dateToHours(change?.endTime ?? profile.end);
                        const preHours = (event.setup + profile.pre) / 60;
                        const postHours = (event.takedown + profile.post) / 60;
                        if (profile.recType === RecordType.RecurrenceGrammar) {
                            const model = ProfileUtil.getProfileModel(null, profile.profileCode, null);
                            const timeSet = {
                                id: `${profile.id}`,
                                dow: change?.pattern ?? ProfileUtil.getShortDowPattern(model),
                                startHour: profileStartHour - preHours,
                                endHour: profileEndHour + postHours,
                                profile,
                                event,
                            };
                            const tooltipData = getEventTooltipData(data, timeSet);
                            return [{ ...timeSet, tooltipData } satisfies TimeSet];
                        } else {
                            const occs = mapOccurrenceDates(profile.reservations);
                            const orderedDowPatterns = BoardUtil.getDowPatterns(occs);
                            const timeSets = EventSummaryService.adhocSummaryModel(
                                occs,
                                -1,
                                99_999,
                                orderedDowPatterns,
                                true,
                                true,
                            );
                            return timeSets.map((timeSet, i) => {
                                const timeSetData = {
                                    id: `${profile.id}-${i}`,
                                    dow: getDowFromTimeSet(timeSet),
                                    startHour: S25Util.date.timeToHours(timeSet.startTime) - preHours,
                                    endHour: S25Util.date.timeToHours(timeSet.endTime) + postHours,
                                    profile,
                                    event,
                                };
                                const tooltipData = getEventTooltipData(data, timeSetData);
                                return { ...timeSetData, tooltipData } satisfies TimeSet;
                            });
                        }
                    }),
            );
    }

    export function getItems(options: {
        data: GridDataResponse;
        timeSets: TimeSet[];
        warningsConfig: Signal<WarningsConfig>;
        mode: Signal<Mode>;
        locations: Signal<Record<number, GridSpace>>;
        rowIndex: Signal<Map<string, number>>;
        allRows: Signal<Row[]>;
        grabbedEntry: Signal<GrabbedEntry | undefined>;
        instructorTimeIndex?: Signal<InstructorTimeIndex>;
    }): Item[] {
        const instructorById = getInstructorById(options.data);
        const items = options.timeSets.map((timeSet) => mapTimeSetToItem({ ...options, timeSet, instructorById }));
        applyBindings(items);

        return items as Item[];
    }

    function mapTimeSetToItem(options: {
        data: GridDataResponse;
        timeSet: TimeSet;
        rowIndex: Signal<Map<string, number>>;
        warningsConfig: Signal<WarningsConfig>;
        mode: Signal<Mode>;
        locations: Signal<Record<number, GridSpace>>;
        instructorById: Record<number, NamedEntity>;
        allRows: Signal<Row[]>;
        grabbedEntry: Signal<GrabbedEntry | undefined>;
        instructorTimeIndex?: Signal<InstructorTimeIndex>;
    }): SourceItem {
        const { data, timeSet } = options;
        return createItem({ ...options, drag: isDraggable(data, timeSet) });
    }

    function isDraggable(data: GridDataResponse, timeSet: TimeSet): { draggable: boolean; message?: string } {
        if (timeSet.event.type === "SPACE_CONFLICT") {
            return {
                draggable: false,
                message: "This is a location conflict not included in the grid's event search",
            };
        }

        if (!hasEditFls(timeSet.event.ols)) {
            return { draggable: false, message: "You lack permission to edit this section" };
        }

        if (timeSet.profile.recType !== RecordType.RecurrenceGrammar) {
            return { draggable: false, message: `Ad hoc sections cannot be moved` };
        }

        const profileModel = ProfileUtil.getProfileModel(null, timeSet.profile.profileCode, null);
        if (profileModel.type !== "weekly") {
            const type = S25Util.firstCharToUpper(profileModel.type);
            return { draggable: false, message: `${type} repeating sections cannot be moved` };
        }

        // Check unassign AP
        const locations = timeSet.profile.reservations.flatMap((res) => res.locations.map((loc) => loc.id));
        const canUnassignLocations = locations.every((loc) => {
            const perm = data.spaces[loc].ap;
            if (perm?.assignOverride) return true;
            return AvailService.isPermEnoughTo("unassign", { assign: perm?.assignPerm, unassign: perm?.unassignPerm });
        });
        if (!canUnassignLocations) {
            return { draggable: false, message: `You lack permission to move one or more location on this section` };
        }

        const resources = timeSet.profile.reservations.flatMap((res) => res.resources.map((res) => res.id));
        const canUnassignResources = resources.every((res) => {
            const perm = data.resourceAp[res];
            if (perm?.assignOverride) return true;
            return AvailService.isPermEnoughTo("unassign", { assign: perm?.assignPerm, unassign: perm?.unassignPerm });
        });
        if (!canUnassignResources) {
            return { draggable: false, message: `You lack permission to move one or more resource on this section` };
        }

        // If locations and resources are not consistent across occurrences we can't easily change the meeting pattern
        // So make those immovable, at least to start with.
        if (!areLocationsAndResourcesConsistent(timeSet.profile.reservations)) {
            const message = `Occurrences in this section do not have consistent location and/or resource reservations`;
            return { draggable: false, message };
        }

        return { draggable: true };
    }

    function areLocationsAndResourcesConsistent(occurrences: GridReservation[]): boolean {
        const allLocations = new Set(occurrences.flatMap((occ) => occ.locations.map((loc) => loc.id)));
        const locationsConsistent = occurrences.every((occ) => {
            if (occ.locations?.length !== allLocations.size) return false;
            return occ.locations.every((loc) => allLocations.has(loc.id));
        });
        const allResources = new Set(occurrences.flatMap((occ) => occ.resources.map((res) => res.id)));
        const resourcesConsistent = occurrences.every((occ) => {
            if (occ.resources?.length !== allResources.size) return false;
            return occ.resources.every((res) => allResources.has(res.id));
        });

        return locationsConsistent && resourcesConsistent;
    }

    function createItem(options: {
        data: GridDataResponse;
        timeSet: TimeSet;
        drag: { draggable: boolean; message?: string };
        rowIndex: Signal<Map<string, number>>;
        warningsConfig: Signal<WarningsConfig>;
        mode: Signal<Mode>;
        locations: Signal<Record<number, GridSpace>>;
        instructorById: Record<number, NamedEntity>;
        allRows: Signal<Row[]>;
        grabbedEntry: Signal<GrabbedEntry | undefined>;
        instructorTimeIndex?: Signal<InstructorTimeIndex>;
    }): SourceItem {
        const { drag, timeSet, rowIndex, mode } = options;

        const itemData = getItemData(options);
        const preHours = ((itemData.setup ?? 0) + (itemData.pre ?? 0)) / 60;
        const postHours = ((itemData.takedown ?? 0) + (itemData.post ?? 0)) / 60;

        const item: SourceItem = {
            id: timeSet.id,
            position: computed(() => itemData.current().position),
            draggable: signal(drag.draggable),
            academicGrid: itemData,
            dowGrid: {
                preHours: signal(preHours),
                postHours: signal(postHours),
                unResizableHours: signal(preHours + postHours),
                snapOffset: signal({ hours: -preHours, top: 0 }),
                noShadows: itemData.noShadows,
            },
            minimapColor: computed(() => {
                const initialized = item.academicGrid.initialized();
                if (!initialized) return;
                return getMinimapColor(initialized);
            }),
            hidden: computed(() => {
                if (item.academicGrid.isLocationConflict) return mode() !== "location";
                if (mode() === "location") return false;
                return itemData.filteredOut();
            }),
            snapOffset: signal({ x: -preHours, y: 0 }),
        };

        return item;
    }

    function getItemData(options: {
        timeSet: TimeSet;
        drag: { draggable: boolean; message?: string };
        warningsConfig: Signal<WarningsConfig>;
        mode: Signal<Mode>;
        locations: Signal<Record<number, GridSpace>>;
        instructorById: Record<number, NamedEntity>;
        rowIndex: Signal<Map<string, number>>;
        allRows: Signal<Row[]>;
        grabbedEntry: Signal<GrabbedEntry | undefined>;
        instructorTimeIndex?: Signal<InstructorTimeIndex>;
    }): ItemData {
        const {
            timeSet,
            drag,
            warningsConfig,
            mode,
            locations,
            instructorById,
            rowIndex,
            allRows,
            grabbedEntry,
            instructorTimeIndex,
        } = options;
        const { profile, event } = timeSet;

        const initialized = signal<Item | undefined>(undefined);

        const isLocationConflict = timeSet.event.type === "SPACE_CONFLICT";
        const hasAdditionalTime = !isLocationConflict;

        const setup = !hasAdditionalTime ? 0 : (timeSet.event.setup ?? 0);
        const pre = !hasAdditionalTime ? 0 : (timeSet.profile.pre ?? 0);
        const post = !hasAdditionalTime ? 0 : (timeSet.profile.post ?? 0);
        const takedown = !hasAdditionalTime ? 0 : (timeSet.event.takedown ?? 0);
        const setupPreHours = (setup + pre) / 60;
        const postTakedownHours = (post + takedown) / 60;

        const current = timeSet.tooltipData.current;
        const original = signal(S25Util.deepCopy(untracked(current)));

        const rowKey = computed(() => {
            const localMode = mode();
            switch (localMode) {
                case "schedule":
                    return String(timeSet.profile.id);
                case "instructor":
                    return String(current().position.instructors[0] ?? "none");
                case "location": {
                    // Conflicts also carry rooms their event occupies outside this grid, which have no row
                    const locations = current().position.locations;
                    const rowLocation = locations.find((locationId) => rowIndex().has(String(locationId)));
                    return String(rowLocation ?? locations[0] ?? "none");
                }
                default:
                    S25Assert.never(localMode, "uncovered mode");
            }
        });
        const effectiveRowKey = computed(() => {
            const localMode = mode();
            switch (localMode) {
                case "schedule":
                    return String(timeSet.profile.id);
                case "instructor":
                    return String(effectivePosition.instructors()[0] ?? "none");
                case "location":
                    return String(effectivePosition.locations()[0] ?? "none");
                default:
                    S25Assert.never(localMode, "uncovered mode");
            }
        });

        const removedOrphanedAlsoAssignLocations = signal<Set<number>>(new Set());
        const swappedLocations = signal<{ old: number; new: number } | undefined>(undefined);
        const candidateDow = computed(() => initialized()?.dowGrid.candidate.position()?.dow);
        const candidateStartHour = computed(() => initialized()?.dowGrid.candidate.position()?.startHour);
        const candidateEndHour = computed(() => initialized()?.dowGrid.candidate.position()?.endHour);
        const changedLocation = computed(() => {
            if (swappedLocations()) return swappedLocations();
            if (mode() !== "location") return;
            const item = initialized();
            const offset = item?.dowGrid.candidate.offset();
            if (!item || !offset) return;

            const grabbed = grabbedEntry();
            const grabbedItem = grabbed?.entry?.source;
            if (!grabbedItem || grabbedItem.id !== item.id) return;

            // Row change follows the grabbed item only, so bound sections keep their rooms.
            const offsetPercent = offset.y;
            const { index, newIndex } = getOffsetRowIndex({
                rowId: grabbed.rowKey,
                offsetPercent,
                rowIndex,
            });
            if (index === undefined || newIndex === undefined) return;

            const rows = allRows();
            const oldLocationId = rows[index].academicGrid.location!.id;
            const newLocationId = rows[newIndex].academicGrid.location!.id;
            return { old: oldLocationId, new: newLocationId };
        });
        const candidateAlsoAssign = computed(() => {
            const location = changedLocation();
            if (!location) return [];

            const shouldAlsoAssign = location.old !== location.new && location.new !== "none";
            const alsoAssign = new Set<number>(
                !shouldAlsoAssign ? [] : locations()[<number>location.new].relationships.alsoAssign,
            );

            const removedLocations = removedOrphanedAlsoAssignLocations();
            const filteredLocations = current().position.locations.filter((loc) => {
                if (loc === location.old && loc !== location.new) return false; // Removed
                return !removedLocations.has(loc);
            });
            // Also assign locations
            for (const loc of filteredLocations) {
                for (const also of locations()[loc].relationships.alsoAssign) alsoAssign.add(also);
            }
            // Remove any already assigned locations
            for (const loc of filteredLocations) alsoAssign.delete(loc);

            return Array.from(alsoAssign);
        });
        const candidatePosition: Signals<{ [K in keyof Position]: Position[K] | undefined }> = {
            dow: candidateDow,
            startHour: candidateStartHour,
            endHour: candidateEndHour,
            locations: computed(() => {
                const item = initialized();
                const history = item?.dowGrid.candidate.position();
                if (history && !item?.dowGrid.candidate.offset()) return history.locations;
                if (mode() !== "location") return current().position.locations;
                const location = changedLocation();
                if (!location) return;

                const newLocations = new Set(current().position.locations);
                if (location.old !== "none") newLocations.delete(location.old);
                if (location.new !== "none") newLocations.add(location.new);
                for (const also of candidateAlsoAssign()) newLocations.add(also);

                const removedLocations = removedOrphanedAlsoAssignLocations();
                return Array.from(newLocations).filter((loc) => !removedLocations.has(loc));
            }),
            instructors: computed(() => {
                if (timeSet.tooltipData.isLocationConflict) return;
                const item = initialized();
                const history = item?.dowGrid.candidate.position();
                const offset = item?.dowGrid.candidate.offset();
                if (history && !offset) return history.instructors;
                if (mode() !== "instructor") return current().position.instructors;
                if (!item || !offset) return;

                const grabbed = grabbedEntry();
                const grabbedItem = grabbed?.entry?.source;
                if (!grabbedItem || grabbedItem.id !== item.id) return;

                const offsetPercent = offset.y;
                const { index, newIndex } = getOffsetRowIndex({
                    rowId: grabbed.rowKey,
                    offsetPercent,
                    rowIndex,
                });
                if (index === undefined || newIndex === undefined) return;

                const rows = allRows();
                const oldInstructorId = rows[index].academicGrid.instructor!.id;
                const newInstructorId = rows[newIndex].academicGrid.instructor!.id;
                return current()
                    .position.instructors.map((id) => (id === oldInstructorId ? newInstructorId : id))
                    .filter((id) => id !== "none");
            }),
        };

        const effectivePosition: Signals<Position> = {
            dow: computed(() => candidatePosition.dow() ?? current().position.dow),
            startHour: computed(() => candidatePosition.startHour() ?? current().position.startHour),
            endHour: computed(() => candidatePosition.endHour() ?? current().position.endHour),
            locations: computed(() => candidatePosition.locations() ?? current().position.locations),
            instructors: computed(() => candidatePosition.instructors() ?? current().position.instructors),
        };

        const effectiveEventTimes = computed(() => {
            const target = { startHour: effectivePosition.startHour(), endHour: effectivePosition.endHour() };
            return getNewEventTimes(current(), target, { setupPreHours, postTakedownHours });
        });
        const rebuiltPattern = computed(() => {
            const baseline = current();
            const dow = effectivePosition.dow();
            if (baseline.position.dow === dow || !baseline.occurrences?.length) return;
            const context = { setup, pre, post, takedown, term: timeSet.tooltipData.term };
            return rebuildPattern(baseline, dow, effectiveEventTimes(), context);
        });
        const effectiveOccurrences = computed(() => {
            const dow = effectivePosition.dow();
            const locs = effectivePosition.locations();
            const context = { setup, pre, post, takedown, term: timeSet.tooltipData.term, locationById: locations() };
            return getNewOccurrences(current(), dow, locs, effectiveEventTimes(), context, rebuiltPattern());
        });
        const effectiveReservationRange = computed(() =>
            reservationDatesFromOccurrences(effectiveOccurrences(), current()),
        );
        const effectiveDerived: Signals<DerivedData> = {
            eventStartHour: computed(() => effectiveEventTimes().eventStartHour),
            eventEndHour: computed(() => effectiveEventTimes().eventEndHour),
            occurrences: effectiveOccurrences,
            profileCode: computed(() => rebuiltPattern()?.profileCode ?? current().profileCode),
            profileName: computed(() =>
                profileNameFromOccurrences(current(), effectivePosition.dow(), effectiveOccurrences()),
            ),
            startDate: computed(() => effectiveReservationRange()[0]),
            endDate: computed(() => effectiveReservationRange()[1]),
            locations: computed(() => getNamedEntity(effectivePosition.locations(), locations(), current().locations)),
            instructors: computed(() =>
                getNamedEntity(effectivePosition.instructors(), instructorById, current().instructors),
            ),
        };
        const candidate: { position: typeof candidatePosition } & Signals<DerivedData> = {
            position: candidatePosition,
            ...effectiveDerived,
        };
        const effective: CandidateSignals = { position: effectivePosition, ...effectiveDerived };

        let issues: Omit<Item["academicGrid"]["issues"], "hasIssues"> | undefined;
        const issuesContext = computed(() => ({
            locations: effective.position.locations(),
            expectedHeadCount: timeSet.tooltipData.expectedHeadCount,
            registeredHeadCount: timeSet.tooltipData.registeredHeadCount,
            warningsConfig: warningsConfig(),
            mode: mode(),
            locationData: locations(),
            featurePreferences: timeSet.tooltipData.featurePreferences,
            partitionPreferences: timeSet.tooltipData.partitionPreferences,
            primaryOrganization: timeSet.tooltipData.primaryOrganization,
        }));
        if (timeSet.tooltipData.isLocationConflict) {
            issues = disabledIssues();
        } else {
            issues = {
                instructorConflicts: computed(() => {
                    const item = initialized();
                    const index = instructorTimeIndex?.();
                    if (!item || !index) return false;
                    const overlaps = getOverlappingInstructors(index, item);
                    return getInstructorConflicts(item, overlaps).length > 0;
                }),
                locationConflicts: signal(false),
                resourceConflicts: signal(false),
                locationUndersized: computed(() => !!getUndersizedLocations(issuesContext()).length),
                locationOversized: computed(() => !!getOversizedLocations(issuesContext()).length),
                standardSchedules: computed(() => !(initialized()?.dowGrid.fitsSchedule?.() ?? true)),
                missingFeaturePrefs: computed(() => !!getMissingFeaturePrefs(issuesContext()).length),
                missingPartitionPrefs: computed(() => !!getMissingPartitionPrefs(issuesContext()).length),
            };
        }

        const filteredOut = signal(false);

        const itemData: ItemData = {
            initialized,
            profileAlienUid: profile.alienUID,
            profileId: profile.id,
            boundProfiles: profile.boundProfiles,
            adHoc: profile.recType !== RecordType.RecurrenceGrammar,
            original,
            dragMessage: drag.draggable ? undefined : drag.message,
            issues: { ...issues, hasIssues: computed(() => hasIssues(issues, warningsConfig())) },
            isMakeupDate: timeSet.profile.alienUID?.startsWith("LYNX-MOD") && timeSet.profile.name.startsWith("MAKEUP"),
            isConversionDate:
                timeSet.profile.alienUID?.startsWith("LYNX-MOD") && timeSet.profile.name.startsWith("CONVERSION"),
            isExamDate: timeSet.profile.alienUID?.startsWith("LYNX-FX-RSRV") && timeSet.profile.name.startsWith("EXAM"),
            ...timeSet.tooltipData,
            setup,
            pre,
            post,
            takedown,
            setupPreHours,
            postTakedownHours,
            profileCount: event.profiles.length,
            rowKey,
            displayAsConflict: computed(() => mode() === "location" && filteredOut()),
            noShadows: computed(() => effectiveRowKey() === "none"),
            candidate,
            effective,
            linkedItems: [],
            filteredOut,
            removedOrphanedAlsoAssignLocations,
            swappedLocations,
            candidateAlsoAssign,
            changedLocation,
        };

        return itemData;
    }

    export function rowKeyFromSnapshot(
        snapshot: Position,
        options: { mode: Mode; profileId: number; rowIndex: ReadonlyMap<string, number> },
    ): string {
        switch (options.mode) {
            case "schedule":
                return String(options.profileId);
            case "instructor":
                return String(snapshot.instructors[0] ?? "none");
            case "location": {
                const rowLocation = snapshot.locations.find((locationId) => options.rowIndex.has(String(locationId)));
                return String(rowLocation ?? snapshot.locations[0] ?? "none");
            }
            default:
                S25Assert.never(options.mode, "uncovered mode");
        }
    }

    export function getOffsetRowIndex(options: {
        rowId: string;
        offsetPercent: number;
        rowIndex: Signal<Map<string, number>>;
    }) {
        const { rowId, offsetPercent, rowIndex } = options;
        const rowIndexMap = rowIndex();
        const index = rowIndexMap.get(rowId)!;
        const offsetIndices = Math.round((rowIndexMap.size * offsetPercent) / 100);
        const newIndex = S25Util.clamp(index + offsetIndices, 0, rowIndexMap.size - 1);
        return { index, newIndex };
    }

    export function occupancyRowFromMembership(
        locationId: number | "none",
        currentLocations: readonly number[],
        effectiveLocations: readonly number[],
    ): number | "none" {
        if (locationId !== "none" && effectiveLocations.includes(locationId)) return locationId;
        const current = new Set(currentLocations);
        const added = effectiveLocations.find((id) => !current.has(id));
        return added ?? "none";
    }

    function getReservationRange(reservations: GridReservation[]): [ISODateString, ISODateString] {
        const earliestReservation = reservations.reduce(
            (earliest, res) => (res.start < earliest ? res.start : earliest),
            reservations[0].start,
        );
        const lastReservation = reservations.reduce(
            (latest, res) => (res.end > latest ? res.end : latest),
            reservations[0].end,
        );
        return [earliestReservation, lastReservation];
    }

    function getDowFromTimeSet(timeSet: EventSummary.TimeSet): string {
        if (timeSet.pattern) {
            const profileCode = EventSummaryService.adhocSummaryModelToProfileCode(timeSet)!;
            const dow = S25Const.dows.filter((dow) => profileCode.includes(dow));
            return BoardUtil.daysOfWeekMap(dow.join(" ") + " ");
        } else {
            let dows = timeSet.map(
                (occ) => S25Util.date.parseDropTZ(occ.reservation_start_dt).getDay() as Proto.DowNumber,
            );
            dows = S25Util.array.unique(dows).sort();
            const dowsAbbr = dows.map((dow) => BOARD_CONST.dowInt2Abbr[dow]);
            return BoardUtil.daysOfWeekMap(dowsAbbr.join(" ") + " ");
        }
    }

    function getEventTooltipData(data: GridDataResponse, timeSet: Omit<TimeSet, "tooltipData">): EventTooltipData {
        const { event, profile } = timeSet;
        const attributeById = new Map(S25Util.array.forceArray(event.attributes).map((ca) => [ca.id, ca]));
        const primaryOrg = event.primaryOrganization;

        const locationIds = new Set<number>();
        for (const res of profile.reservations) {
            for (const location of res.locations || []) {
                if (locationIds.has(location.id)) continue;
                locationIds.add(location.id);
            }
        }
        const locations = [...locationIds].map((id) => ({ id, name: data.spaces[id]?.name }));

        const isLynx = AcademicGrid.isLynx(event);
        const termName = attributeById.get(CustomAttributes.system.termCode.id)?.value;
        const instructor = getEventInstructor(event);

        const [earliestReservation, lastReservation] = getReservationRange(profile.reservations);
        const occurrences = profile.reservations.map((res) => ({
            ...res,
            locations: res.locations
                .filter((loc) => data.spaces[loc.id])
                .map((loc) => ({ ...loc, ap: data.spaces[loc.id].ap })),
            resources: res.resources
                .filter((res) => data.resourceAp[res.id])
                .map((res) => ({ ...res, ap: data.resourceAp[res.id] })),
        }));

        return {
            current: signal<CandidateData>({
                position: {
                    dow: timeSet.dow,
                    startHour: timeSet.startHour,
                    endHour: timeSet.endHour,
                    instructors: instructor ? [instructor.id] : [],
                    locations: locations.map((loc) => loc.id),
                },
                eventStartHour: timeSet.startHour + (event.setup + profile.pre) / 60,
                eventEndHour: timeSet.endHour - (profile.post + event.takedown) / 60,
                occurrences,
                profileCode: profile.profileCode,
                profileName: profile.name,
                startDate: earliestReservation,
                endDate: lastReservation,
                locations,
                instructors: instructor ? [instructor] : [],
            }),
            alienUid: event.alienUid,
            eventId: event.id,
            eventName: event.name,
            eventTitle: event.title ?? "",
            reference: event.locator ?? "",
            isLynx,
            expectedHeadCount: Number(profile.expectedHeadCount ?? 0),
            registeredHeadCount: Number(profile.registeredHeadCount ?? 0),
            featurePreferences: timeSet.profile.featurePreferences,
            partitionPreferences: timeSet.profile.partitionPreferences,
            subjectCode: isLynx ? primaryOrg?.name : undefined,
            courseReferenceNumber: attributeById.get(CustomAttributes.system.uniqueSectionId.id)?.value,
            subTerm: attributeById.get(CustomAttributes.system.subTerm.id)?.value,
            sectionType: attributeById.get(CustomAttributes.system.sectionType.id)?.value,
            term: termName ? data.terms[termName] : undefined,
            primaryOrganization: primaryOrg,
            isLocationConflict: timeSet.event.type === "SPACE_CONFLICT",
            setup: event.setup ?? 0,
            pre: profile.pre ?? 0,
            post: profile.post ?? 0,
            takedown: event.takedown ?? 0,
        };
    }

    export function isLynx(event: GridEvent) {
        return event.alienUid?.toLowerCase().startsWith("lynx");
    }

    function getEventInstructor(event: GridEvent): NamedEntity | undefined {
        const role = S25Util.array.forceArray(event.roles).find((role) => role.role === S25Const.instructorRole.event);
        if (!role) return;
        return { id: role.id, name: getContactName(role) };
    }

    export function getInstructorById(data: GridDataResponse): Record<number, NamedEntity> {
        const instructorById: Record<number, NamedEntity> = {};
        for (const event of data?.events ?? []) {
            const instructor = getEventInstructor(event);
            if (instructor) instructorById[instructor.id] = instructor;
        }
        return instructorById;
    }

    export function getContactName(contact: { firstName: string; familyName: string }) {
        if (!contact.firstName && !contact.familyName) return "";
        if (!contact.firstName) return contact.familyName.trim();
        if (!contact.familyName) return contact.firstName.trim();
        return `${contact.familyName?.trim()}, ${contact.firstName?.trim()}`;
    }

    function hasIssues(issues: Omit<Item["academicGrid"]["issues"], "hasIssues">, warningsConfig: WarningsConfig) {
        return Object.values(itemIssueTypes).some(({ id }) => {
            if (warningsConfig[id].action === "ignore") return false;
            return issues[id]();
        });
    }

    export function getUndersizedLocations(options: {
        locations: number[];
        expectedHeadCount: number;
        registeredHeadCount: number;
        warningsConfig: WarningsConfig;
        mode: Mode;
        locationData: Record<number, GridSpace>;
    }): number[] {
        const threshold = options.warningsConfig.locationUndersized.threshold / 100;
        return getLocationFillRatios({ ...options, ratio: options.warningsConfig.locationUndersized.ratio })
            .filter((loc) => loc.fillRatio > threshold)
            .map((loc) => loc.id);
    }

    export function getOversizedLocations(options: {
        locations: number[];
        expectedHeadCount: number;
        registeredHeadCount: number;
        warningsConfig: WarningsConfig;
        mode: Mode;
        locationData: Record<number, GridSpace>;
    }): number[] {
        const threshold = options.warningsConfig.locationOversized.threshold / 100;
        return getLocationFillRatios({ ...options, ratio: options.warningsConfig.locationOversized.ratio })
            .filter((loc) => loc.fillRatio < threshold)
            .map((loc) => loc.id);
    }

    function getLocationFillRatios(options: {
        locations: number[];
        expectedHeadCount: number;
        registeredHeadCount: number;
        ratio: WarningHeadCountRatio;
        mode: Mode;
        locationData: Record<number, GridSpace>;
    }) {
        const { ratio, locations, locationData } = options;
        if (!locations.length) return [];

        const headCountKey = ratio === "RHC:CAP" ? "registeredHeadCount" : "expectedHeadCount";
        const headCount = options[headCountKey] ?? 0;

        return locations.map((id) => ({ id, fillRatio: headCount / locationData[id]!.maxCapacity }));
    }

    export function getMissingFeaturePrefs(options: {
        mode: Mode;
        locationData: Record<number, GridSpace>;
        locations: number[];
        featurePreferences: EventTooltipData["featurePreferences"];
    }): { id: number; missingPrefs: string[] }[] {
        const prefs = options.featurePreferences;
        if (!prefs.length) return [];
        return options.locations
            .map((id) => {
                const location = options.locationData[id];
                const features = location?.features ?? [];
                const missing = S25Util.array.relativeComplement(features, prefs, (item) => item.id);
                return { id, missingPrefs: missing.map((pref) => pref.name) };
            })
            .filter(({ missingPrefs }) => missingPrefs.length > 0);
    }

    export function getMissingPartitionPrefs(options: {
        mode: Mode;
        locationData: Record<number, GridSpace>;
        locations?: number[];
        partitionPreferences: EventTooltipData["partitionPreferences"];
        primaryOrganization: EventTooltipData["primaryOrganization"];
    }): number[] {
        const sectionPrefs = options.partitionPreferences;
        const orgPrefs = options.primaryOrganization?.partitionPreferences?.flat() ?? [];
        const prefs = new Set([...sectionPrefs, ...orgPrefs].map((pref) => pref.id));
        if (!prefs.size) return [];
        return options.locations!.filter((id) => {
            const partId = options.locationData[id]!.partition?.id;
            return !partId || !prefs.has(partId);
        });
    }

    function disabledIssues(): Omit<ItemData["issues"], "hasIssues"> {
        return {
            instructorConflicts: signal(false),
            locationConflicts: signal(false),
            resourceConflicts: signal(false),
            locationUndersized: computed(() => false),
            locationOversized: computed(() => false),
            standardSchedules: computed(() => false),
            missingFeaturePrefs: computed(() => false),
            missingPartitionPrefs: computed(() => false),
        };
    }

    // Needs to be kept up to date with legend
    function getMinimapColor(item: Item) {
        if (item.academicGrid.displayAsConflict()) return "#4a4a4a";
        if (item.academicGrid.issues.instructorConflicts()) return "#ba92e1";
        if (item.academicGrid.issues.locationConflicts()) return "#0077d7";
        if (item.academicGrid.issues.resourceConflicts()) return "#e91e63";
        if (item.academicGrid.issues.locationUndersized()) return "#009fff";
        if (item.academicGrid.issues.locationOversized()) return "#009fff";
        if (item.academicGrid.issues.missingFeaturePrefs()) return "#ffff39";
        if (item.academicGrid.issues.missingPartitionPrefs()) return "#78ce3d";
        if (item.academicGrid.issues.standardSchedules()) return "#ff5959";
        if (item.academicGrid.adHoc) return "#d2d2d2";
        if (!!item.academicGrid.current().locations.length) return "#bbdfff";
        return "#cff3d2";
    }

    function mapOccurrenceDates(occs: GridReservation[]) {
        return occs.map((occ) => ({ reservation_start_dt: occ.start, reservation_end_dt: occ.end }));
    }

    function applyBindings(items: SourceItem[]) {
        const itemsByProfile = S25Util.array.groupBy(items, (item) => item.academicGrid.profileId);

        for (const item of items) {
            if (!item.academicGrid.boundProfiles?.length) continue;
            for (const profile of item.academicGrid.boundProfiles) {
                const boundItems = itemsByProfile[profile];
                for (const binding of boundItems ?? []) {
                    if (item.academicGrid.current().position.dow !== binding.academicGrid.current().position.dow)
                        continue;

                    const overlapping = isOverlapping(
                        item.academicGrid.current().position,
                        binding.academicGrid.current().position,
                    );
                    const sameTime =
                        item.academicGrid.current().position.startHour ===
                            binding.academicGrid.current().position.startHour &&
                        item.academicGrid.current().position.endHour ===
                            binding.academicGrid.current().position.endHour;
                    let dragMessage;
                    if (!overlapping) {
                        dragMessage = `Section is subject to a back to back binding. To move this section break the binding through LYNX`;
                    } else if (!sameTime) {
                        dragMessage = `Section is overlapping with another bound section. To move this section break the binding through LYNX`;
                    }
                    if (!dragMessage) {
                        item.academicGrid.linkedItems.push(<Item>binding);
                        binding.academicGrid.linkedItems.push(<Item>item);
                    } else {
                        item.draggable = signal(false);
                        binding.draggable = signal(false);
                        item.academicGrid.dragMessage = dragMessage;
                        binding.academicGrid.dragMessage = dragMessage;
                    }
                }
            }
        }
    }

    function isOverlapping(a: Position, b: Position) {
        return a.dow === b.dow && a.startHour <= b.endHour && a.endHour >= b.startHour;
    }

    export function applyChange(item: Item, change: GridItemChange) {
        if (!change || !item.draggable?.() || !item.academicGrid) return;
        const eventStartHour = S25Util.date.dateToHours(change.startTime);
        const eventEndHour = S25Util.date.dateToHours(change.endTime);
        const paddingStart = (item.academicGrid.setup + item.academicGrid.pre) / 60;
        const paddingEnd = (item.academicGrid.post + item.academicGrid.takedown) / 60;
        const baseline = item.academicGrid.current();
        item.academicGrid.current.set(
            getUpdatedEventData(
                baseline,
                {
                    dow: change.pattern,
                    startHour: eventStartHour - paddingStart,
                    endHour: eventEndHour + paddingEnd,
                    locations: baseline.position.locations,
                    instructors: baseline.position.instructors,
                },
                {
                    setup: item.academicGrid.setup,
                    pre: item.academicGrid.pre,
                    post: item.academicGrid.post,
                    takedown: item.academicGrid.takedown,
                    term: item.academicGrid.term,
                    locationById: {},
                    instructorById: {},
                },
            ),
        );
        // TODO MULTIPLAYER: Have dow grid update its properties
    }

    export function readCandidate(bundle: CandidateSignals): CandidateData {
        return {
            position: {
                dow: bundle.position.dow(),
                startHour: bundle.position.startHour(),
                endHour: bundle.position.endHour(),
                locations: bundle.position.locations(),
                instructors: bundle.position.instructors(),
            },
            eventStartHour: bundle.eventStartHour(),
            eventEndHour: bundle.eventEndHour(),
            occurrences: bundle.occurrences(),
            profileCode: bundle.profileCode(),
            profileName: bundle.profileName(),
            startDate: bundle.startDate(),
            endDate: bundle.endDate(),
            locations: bundle.locations(),
            instructors: bundle.instructors(),
        };
    }

    type EventTimes = {
        startHour: number;
        endHour: number;
        eventStartHour: number;
        eventEndHour: number;
    };

    type RebuiltPattern = {
        occurrences: Occurrence[];
        profileCode: string;
    };

    function getNewEventTimes(
        baseline: CandidateData,
        target: Pick<Position, "startHour" | "endHour">,
        additionalTime: { setupPreHours: number; postTakedownHours: number },
    ): EventTimes {
        const paddedEventStartHour = target.startHour + additionalTime.setupPreHours;
        const paddedEventEndHour = target.endHour - additionalTime.postTakedownHours;
        const startDelta = paddedEventStartHour - baseline.eventStartHour;
        let endDelta = paddedEventEndHour - baseline.eventEndHour;

        const startHour = baseline.position.startHour + startDelta;
        let endHour = baseline.position.endHour + endDelta;
        if (endHour <= startHour) {
            endHour += 24;
            endDelta = endHour - baseline.position.endHour;
        }
        return {
            startHour,
            endHour,
            eventStartHour: baseline.eventStartHour + startDelta,
            eventEndHour: baseline.eventEndHour + endDelta,
        };
    }

    function rebuildPattern(
        baseline: CandidateData,
        newDow: string,
        newEventTimes: EventTimes,
        context: {
            setup: number;
            pre: number;
            post: number;
            takedown: number;
            term?: GridDataTerm;
        },
    ): RebuiltPattern {
        const newDows = new Set(newDow.split(""));
        const lastOccurrence = baseline.occurrences[baseline.occurrences.length - 1];
        const defaultThroughDate =
            (context.term?.end_dt && S25Util.date.parse(context.term.end_dt)) ||
            (lastOccurrence?.end && S25Util.date.parseDropTZ(lastOccurrence.end)) ||
            (baseline.endDate && S25Util.date.parseDropTZ(baseline.endDate)) ||
            new Date();
        const profileModel = ProfileUtil.getProfileModel(null, baseline.profileCode, defaultThroughDate);
        for (const dayOfWeek of profileModel.daysOfWeek!) {
            dayOfWeek.chosen = newDows.has(BOARD_CONST.dowMap[dayOfWeek.abbr as keyof typeof BOARD_CONST.dowMap]);
        }

        // Set start and end date to the correct date for the first occurrence
        const firstDowIndex = profileModel.daysOfWeek!.findIndex((dayOfWeek) => dayOfWeek.chosen);
        let startDt = S25Util.date.changeDow(S25Util.date.parseDropTZ(baseline.occurrences[0].start), firstDowIndex);

        // If start date is before term start, place start date on the first dow after start
        const termStart = context.term?.start_dt ? S25Util.date.parse(context.term.start_dt) : undefined;
        if (context.term && termStart && termStart > startDt) {
            const termStartDow = termStart.getDay();
            const newDowIndex = profileModel.daysOfWeek!.findIndex(
                (dayOfWeek) => dayOfWeek.chosen && Number(dayOfWeek.itemId) >= termStartDow,
            );
            if (newDowIndex !== -1) {
                startDt = S25Util.date.changeDow(termStart, newDowIndex);
            } else {
                startDt = S25Util.date.changeDow(termStart, firstDowIndex);
                startDt = S25Util.date.addWeeks(startDt, 1);
            }
        }

        const eventStartTime = S25Util.date.parseDropTZ(
            S25Util.date.toS25ISODateTimeStrFromHours(newEventTimes.eventStartHour),
        );
        const eventEndTime = S25Util.date.parseDropTZ(
            S25Util.date.toS25ISODateTimeStrFromHours(newEventTimes.eventEndHour),
        );
        S25Util.date.syncDateToTime(startDt, eventStartTime);
        const duration = S25Util.date.diffMinutes(eventStartTime, eventEndTime);
        const endDt = S25Util.date.addMinutes(S25Util.date.clone(startDt), duration);

        // Get new occurrences
        const timeModel = {
            evStartDt: startDt,
            evEndDt: endDt,
            minutes: {
                setupMinutes: context.setup,
                preMinutes: context.pre,
                postMinutes: context.post,
                takedownMinutes: context.takedown,
            },
        };
        let rebuiltOccs = ProfileUtil.getOccurrences(timeModel, profileModel);

        // Remove any occurrences after term end date
        if (context.term?.end_dt) {
            const termEnd = S25Util.date.toEndOfDay(context.term.end_dt);
            rebuiltOccs = rebuiltOccs.filter((occ) => occ.evEndDt <= termEnd);
        }

        const resources = baseline.occurrences[0].resources;
        return {
            occurrences: rebuiltOccs.map(
                (occ, occIndex) =>
                    <Occurrence>{
                        id: baseline.occurrences[occIndex]?.id,
                        start: S25Util.date.toS25ISODateTimeStrFromDate(occ.evStartDt),
                        end: S25Util.date.toS25ISODateTimeStrFromDate(occ.evEndDt),
                        commentId: undefined,
                        locations: [],
                        resources: S25Util.deepCopy(resources),
                    },
            ),
            profileCode: ProfileUtil.getProfileCodeByModel(profileModel),
        };
    }

    function getNewOccurrences(
        baseline: CandidateData,
        dow: string,
        locations: number[],
        occupancyHours: EventTimes,
        context: {
            setup: number;
            pre: number;
            post: number;
            takedown: number;
            term?: GridDataTerm;
            locationById: Record<number, GridSpace>;
        },
        rebuiltPattern: RebuiltPattern | undefined,
    ): Occurrence[] {
        const startDelta = occupancyHours.eventStartHour - baseline.eventStartHour;
        const endDelta = occupancyHours.eventEndHour - baseline.eventEndHour;

        let occurrences = baseline.occurrences;
        if (baseline.position.dow === dow) {
            occurrences = baseline.occurrences.map((occ) => ({
                ...occ,
                start: S25Util.date.addHoursToS25ISODateTimeStr(occ.start, startDelta),
                end: S25Util.date.addHoursToS25ISODateTimeStr(occ.end, endDelta),
            }));
        } else if (!baseline.occurrences?.length) {
            occurrences = [];
        } else {
            occurrences =
                rebuiltPattern?.occurrences ?? rebuildPattern(baseline, dow, occupancyHours, context).occurrences;
        }

        const occurrenceLocations = occurrenceLocationsFromPosition(
            baseline.occurrences,
            locations,
            context.locationById,
        );
        occurrences = occurrences.map((occ) => ({
            ...occ,
            locations: S25Util.deepCopy(occurrenceLocations),
        }));

        if (occurrences?.length) occurrences.sort(occurrenceSort);
        return occurrences;
    }

    function reservationDatesFromOccurrences(
        occurrences: Occurrence[],
        baseline: CandidateData,
    ): [ISODateString, ISODateString] {
        if (!occurrences?.length) return [baseline.startDate, baseline.endDate];
        return getReservationRange(occurrences);
    }

    function profileNameFromOccurrences(baseline: CandidateData, dow: string, occurrences: Occurrence[]): string {
        const firstOccurrence = occurrences[0] ?? baseline.occurrences[0];
        return getNewProfileName(
            baseline.profileName,
            dow,
            S25Util.date.toS25ISODateStr(firstOccurrence.start)!,
            S25Util.date.toS25ISOTimeStr(firstOccurrence.start),
            S25Util.date.toS25ISOTimeStr(firstOccurrence.end),
        );
    }

    export function getUpdatedEventData(
        baseline: CandidateData,
        target: Position,
        context: {
            setup: number;
            pre: number;
            post: number;
            takedown: number;
            term?: GridDataTerm;
            locationById: Record<number, GridSpace>;
            instructorById: Record<number, NamedEntity>;
        },
    ): CandidateData {
        const extraTime = {
            setupPreHours: (context.setup + context.pre) / 60,
            postTakedownHours: (context.post + context.takedown) / 60,
        };
        const occupancyHours = getNewEventTimes(baseline, target, extraTime);
        const weeklyRebuild =
            baseline.position.dow === target.dow || !baseline.occurrences?.length
                ? undefined
                : rebuildPattern(baseline, target.dow, occupancyHours, context);
        const occurrences = getNewOccurrences(
            baseline,
            target.dow,
            target.locations,
            occupancyHours,
            context,
            weeklyRebuild,
        );
        const [startDate, endDate] = reservationDatesFromOccurrences(occurrences, baseline);
        return {
            position: {
                dow: target.dow,
                startHour: occupancyHours.startHour,
                endHour: occupancyHours.endHour,
                locations: target.locations,
                instructors: target.instructors,
            },
            eventStartHour: occupancyHours.eventStartHour,
            eventEndHour: occupancyHours.eventEndHour,
            occurrences,
            profileCode: weeklyRebuild?.profileCode ?? baseline.profileCode,
            profileName: profileNameFromOccurrences(baseline, target.dow, occurrences),
            startDate,
            endDate,
            locations: getNamedEntity(target.locations, context.locationById, baseline.locations),
            instructors: getNamedEntity(target.instructors, context.instructorById, baseline.instructors),
        };
    }

    function occurrenceLocationsFromPosition(
        baselineOccurrences: Occurrence[],
        locationIds: number[],
        locationById: Record<number, GridSpace>,
    ): Occurrence["locations"] {
        const byId = new Map((baselineOccurrences[0]?.locations ?? []).map((location) => [location.id, location]));
        return locationIds.map((locationId) => {
            const existing = byId.get(locationId);
            if (existing) return S25Util.deepCopy(existing);
            const space = locationById[locationId];
            return {
                id: locationId,
                shared: space?.alwaysShared ?? false,
                layoutId: space?.defaultLayoutId,
                instructionsId: space?.defaultInstructionsId,
                ap: space?.ap,
            };
        });
    }

    function getNamedEntity(
        ids: number[],
        entityById: Record<number, NamedEntity>,
        baselineNamed: NamedEntity[],
    ): NamedEntity[] {
        const nameById = new Map(baselineNamed.map((entity) => [entity.id, entity.name]));
        return ids.map((id) => ({
            id,
            name: entityById[id]?.name ?? nameById.get(id) ?? "",
        }));
    }

    function occurrenceSort(a: Occurrence, b: Occurrence) {
        if (a.start === b.start) return 0;
        return a.start < b.start ? -1 : 1;
    }

    export function getScheduleRows(timeSets: TimeSet[]): Row[] {
        const rows: Row[] = [];
        const seen = new Set<number>();
        for (const timeSet of timeSets) {
            const id = timeSet.profile.id;
            if (seen.has(id)) continue;
            seen.add(id);
            let header = timeSet.event.name;
            if (timeSet.event.profiles.length > 1) header += ` (${timeSet.profile.name})`;
            const hidden = signal(false);
            rows.push({
                id: `${id}`,
                heading: header,
                hidden: hidden.asReadonly(),
                academicGrid: {
                    hidden,
                    schedule: {
                        ...timeSet.tooltipData,
                        header,
                        id,
                        profileId: timeSet.profile.id,
                    },
                },
            });
        }
        rows.sort((a, b) => a.academicGrid.schedule!.header.localeCompare(b.academicGrid.schedule!.header));
        return rows;
    }

    export function getLocationConflicts(options: {
        data: GridDataResponse;
        warningsConfig: Signal<WarningsConfig>;
        mode: Signal<Mode>;
        locations: Signal<Record<number, GridSpace>>;
        rowIndex: Signal<Map<string, number>>;
        allRows: Signal<Row[]>;
        grabbedEntry: Signal<GrabbedEntry | undefined>;
    }) {
        if (!options.data) return [];

        const timeSets = getTimeSets(options.data, options.data.spaceConflicts).filter(
            (set) => !!untracked(set.tooltipData.current).position.locations.length,
        );
        return getItems({ ...options, timeSets });
    }

    export function getInstructorRows(instructors: NamedEntity[]): Row[] {
        const rows: Row[] = [];
        const seen = new Set<number>();
        for (const instructor of instructors) {
            if (seen.has(instructor.id)) continue;
            seen.add(instructor.id);
            const hidden = signal(false);
            rows.push({
                id: `${instructor.id}`,
                heading: instructor.name,
                hidden: hidden.asReadonly(),
                academicGrid: { hidden, instructor: { id: instructor.id, name: instructor.name! } },
            });
        }
        rows.sort((a, b) => a.academicGrid.instructor!.name.localeCompare(b.academicGrid.instructor!.name));
        const hidden = signal(false);
        rows.unshift({
            id: "none",
            heading: "Unassigned",
            hidden: hidden.asReadonly(),
            academicGrid: { hidden, instructor: { id: "none", name: "Unassigned" } },
        });
        return rows;
    }

    export function getLocationRows(locations: GridSpace[]): Row[] {
        const rows: Row[] = locations.map((location) => {
            const hidden = signal(false);
            return {
                id: `${location.id}`,
                heading: location.name,
                hidden: hidden.asReadonly(),
                academicGrid: { hidden, location: { id: location.id, name: location.name } },
            };
        });
        const hidden = signal(false);
        rows.unshift({
            id: "none",
            heading: "Unassigned",
            hidden: hidden.asReadonly(),
            academicGrid: { hidden, location: { id: "none", name: "Unassigned" } },
        });
        return rows;
    }

    export function sortLocations(options: {
        rows: Row[];
        sortItem: Item | undefined;
        prioritizedLocations: Set<number | "none"> | undefined;
        warningsConfig: WarningsConfig;
        locations: Record<number, GridSpace>;
    }) {
        const { sortItem, prioritizedLocations, warningsConfig, locations } = options;
        const rows = options.rows.slice().sort((a, b) => {
            if (a.academicGrid.location!.id === "none") return -1;
            if (b.academicGrid.location!.id === "none") return 1;
            return a.heading!.localeCompare(b.heading!);
        });
        if (!sortItem && !prioritizedLocations) return rows;

        const featurePreferences = new Set(
            sortItem?.academicGrid.featurePreferences?.map((feature) => feature.id) ?? [],
        );

        const partitionPriorities = [...(sortItem?.academicGrid.primaryOrganization?.partitionPreferences ?? [])];
        if (!!sortItem?.academicGrid.partitionPreferences) {
            partitionPriorities.unshift(sortItem?.academicGrid.partitionPreferences);
        }
        const partitionPriority = new Map(
            partitionPriorities.flatMap((partitions, priority) => {
                return partitions.map((partition) => [partition.id, priority + 1]);
            }),
        );

        const context = {
            sortItem,
            featurePreferences,
            partitionPriority,
            headCount: sortItem?.academicGrid.expectedHeadCount ?? 0,
            minFillRatio: warningsConfig.locationUndersized.threshold / 100,
        };

        // Rows only carry the location's id and name, so scoring needs the full space from the grid data
        const scoreRow = (row: Row) => {
            const locationId = row.academicGrid.location!.id;
            const location = locationId === "none" ? undefined : locations[locationId];
            return location ? scoreLocation(location, context) : -Infinity;
        };

        return rows.sort((a, b) => {
            if (a.academicGrid.location!.id === "none") return -1;
            if (b.academicGrid.location!.id === "none") return 1;
            if (!!sortItem) {
                const scoreA = scoreRow(a);
                const scoreB = scoreRow(b);
                if (scoreA !== scoreB) return scoreB - scoreA;
                return 0;
            }
            if (prioritizedLocations) {
                const A = prioritizedLocations.has(a.academicGrid.location!.id) ? -1 : 0;
                const B = prioritizedLocations.has(b.academicGrid.location!.id) ? -1 : 0;
                if (A !== B) return A - B;
            }

            return 0;
        });
    }

    export function scoreLocation(
        location: GridSpace,
        context: {
            sortItem: Item | undefined;
            featurePreferences: Set<number>;
            partitionPriority: Map<number, number>;
            headCount: number;
            minFillRatio: Flavor<number, "[0-1]">;
        },
    ) {
        // If the item is currently in this location, give it the highest score
        const sortItemLocations = new Set(context.sortItem?.position().locations);
        if (sortItemLocations.has(location.id)) return Number.MAX_SAFE_INTEGER;

        // Score features based on how many of them the location has
        const matchingFeatures = S25Util.array.count(
            location.features,
            (feature) => +context.featurePreferences.has(feature.id),
        );

        // Score partition based on priority, where higher priority is worse
        const priority = location.partition?.id ? context.partitionPriority.get(location.partition.id) : undefined;
        const partitionScore = priority ? 1 / priority : 0;

        // Score utilization
        const unusedHeadcount = location.maxCapacity - context.headCount;
        let utilizationScore = 1 / (Math.abs(unusedHeadcount) || 0.9); // 0 => 0.9 to avoid division by 0 and to keep scores manageable
        if (unusedHeadcount < 0) utilizationScore -= 0.0001; // Minor penalty for being over capacity
        if (context.headCount / (location.maxCapacity || 0.001) < context.minFillRatio) utilizationScore -= 1; // Major penalty for being less than the min fill ratio

        // Add scores up
        return matchingFeatures + partitionScore + utilizationScore;
    }

    export function getFilters(options: {
        hasLocationFilters: Signal<boolean>;
        dowOptions: Extractable<string>[];
        featurePrefs: Extractable<number>[];
        partitionPrefs: Extractable<number>[];
        instructors: Extractable<number>[];
        sectionTypes: Extractable<number>[];
        subjectCodes: Extractable<string>[];
        subTerms: Extractable<number>[];
        partitions: Extractable<number>[];
        features: Extractable<number>[];
        locations: Record<number, GridSpace>;
    }) {
        const toDropdowns = <T extends number | string>(items: Extractable<T>[]) =>
            GenericDropdownUtil.extractItems(items) ?? [];
        return {
            items: {
                label: "Sections",
                filters: [
                    createFilter<"item">({
                        type: "text",
                        label: "Name",
                        transform: (item) => item.academicGrid?.eventName,
                    }),
                    createFilter<"item">({
                        id: "dows",
                        type: "dropdownMultiselect",
                        label: "Meeting Patterns",
                        transform: (item) => [item.academicGrid?.current().position.dow],
                        data: { items: toDropdowns(options.dowOptions), placeholder: "Meeting Patterns" },
                    }),
                    createFilter<"item">({
                        type: "numberRange",
                        label: "EHC",
                        transform: (item) => item.academicGrid?.expectedHeadCount,
                    }),
                    createFilter<"item">({
                        type: "numberRange",
                        label: "RHC",
                        transform: (item) => item.academicGrid?.registeredHeadCount,
                    }),
                    createFilter<"item">({
                        type: "dropdownMultiselect",
                        label: "Feature Preferences",
                        transform: (item) => item.academicGrid?.featurePreferences.map((pref) => pref.id),
                        data: { items: toDropdowns(options.featurePrefs), placeholder: "Features" },
                    }),
                    createFilter<"item">({
                        type: "dropdownMultiselect",
                        label: "Partition Preferences",
                        transform: (item) => {
                            const sectionPrefs = item.academicGrid?.partitionPreferences ?? [];
                            const orgPrefs = item.academicGrid?.primaryOrganization?.partitionPreferences.flat() ?? [];
                            return [...sectionPrefs, ...orgPrefs].map((pref) => pref.id);
                        },
                        data: { items: toDropdowns(options.partitionPrefs), placeholder: "Partitions" },
                    }),
                    createFilter<"item">({
                        type: "dropdownMultiselect",
                        label: "Instructor",
                        transform: (item) => item.academicGrid.current().position.instructors,
                        data: { items: toDropdowns(options.instructors), placeholder: "Contacts" },
                    }),
                    createFilter<"item">({
                        type: "dropdownMultiselect",
                        label: "Section Type",
                        transform: (item) => [item.academicGrid?.sectionType],
                        data: { items: toDropdowns(options.sectionTypes), placeholder: "Section Types" },
                    }),
                    createFilter<"item">({
                        type: "dropdownMultiselect",
                        label: "Subject Code",
                        transform: (item) => [item.academicGrid?.subjectCode],
                        data: { items: toDropdowns(options.subjectCodes), placeholder: "Subjects" },
                    }),
                    createFilter<"item">({
                        type: "dropdownMultiselect",
                        label: "Subterm",
                        transform: (item) => [item.academicGrid?.subTerm],
                        data: { items: toDropdowns(options.subTerms), placeholder: "Subterms" },
                    }),
                    createFilter<"item">({
                        type: "text",
                        label: "CRN",
                        transform: (item) => item.academicGrid?.courseReferenceNumber,
                    }),
                    createFilter<"item">({
                        type: "options",
                        label: "Include",
                        keepRow: false,
                        transform: (item) => {
                            const { isMakeupDate, isConversionDate, isExamDate } = item.academicGrid ?? {};
                            const skip = !isMakeupDate && !isConversionDate && !isExamDate;
                            return { skip, isMakeupDate, isConversionDate, isExamDate };
                        },
                        data: {
                            items: [
                                { itemId: "isMakeupDate", itemName: "Makeup Dates" },
                                { itemId: "isConversionDate", itemName: "Conversion Dates" },
                                { itemId: "isExamDate", itemName: "Exams Dates" },
                            ],
                            placeholder: "Sections",
                        },
                    }),
                    createFilter<"item">({
                        type: "yesNo",
                        label: "Has Section Partition Preferences",
                        transform: (item) => !!item.academicGrid?.partitionPreferences?.length,
                    }),
                    createFilter<"item">({
                        type: "yesNo",
                        label: "Has Locations",
                        transform: (item) => !!item.academicGrid?.current().position.locations.length,
                    }),
                    createFilter<"item">({
                        type: "yesNo",
                        label: "Has Issues",
                        transform: (item) => item.academicGrid?.issues.hasIssues(),
                    }),
                ],
            },
            locations: {
                label: "Locations",
                hidden: computed(() => !options.hasLocationFilters()),
                filters: [
                    createFilter<"row">({
                        type: "text",
                        label: "Name",
                        transform: (row) => row.academicGrid.location?.name,
                    }),
                    createFilter<"row">({
                        type: "numberRange",
                        label: "Capacity",
                        transform: (row) => {
                            const locationId = row.academicGrid.location?.id;
                            if (!locationId || locationId === "none") return undefined;
                            return options.locations[locationId]?.maxCapacity;
                        },
                    }),
                    createFilter<"row">({
                        type: "dropdownMultiselect",
                        label: "Features",
                        transform: (row) => {
                            const locationId = row.academicGrid.location?.id;
                            if (!locationId || locationId === "none") return [];
                            return options.locations[locationId]?.features.map((f) => f.id);
                        },
                        data: { items: toDropdowns(options.features), placeholder: "Features" },
                    }),
                    createFilter<"row">({
                        type: "dropdownMultiselect",
                        label: "Partitions",
                        transform: (row) => {
                            const locationId = row.academicGrid.location?.id;
                            if (!locationId || locationId === "none") return [];
                            const partitionId = options.locations[locationId]?.partition?.id;
                            return !partitionId ? [] : [partitionId];
                        },
                        data: { items: toDropdowns(options.partitions), placeholder: "Partitions" },
                    }),
                ],
            },
        };
    }

    function createFilter<ItemType extends "item" | "row" = "item">(options: {
        id?: string;
        type: Exclude<Filter["type"], "none">;
        label: string;
        transform?: (item: ItemType extends "item" ? Item : Row) => any;
        data?: MultiselectFilterData;
        keepRow?: boolean;
    }): Filter<ItemType> {
        const { id, type, label, transform, data, keepRow } = options;
        switch (type) {
            case "text":
                const textValue = signal<string | undefined>(undefined);
                return {
                    id,
                    keepRow,
                    label,
                    type,
                    value: textValue,
                    active: computed(() => !!textValue()),
                    clear: () => textValue.set(undefined),
                    text: computed(() => textValue() as string),
                    filter: (item) => !!transform?.(item)?.toLowerCase().includes(textValue()?.toLowerCase()),
                };
            case "numberRange":
                const numberRange = signal<{ min: WritableSignal<number | null>; max: WritableSignal<number | null> }>({
                    min: signal(null),
                    max: signal(null),
                });
                return {
                    id,
                    keepRow,
                    label,
                    type,
                    value: numberRange,
                    active: computed(() => numberRange().min() !== null || numberRange().max() !== null),
                    clear: () => numberRange.set({ min: signal(null), max: signal(null) }),
                    text: computed(() => {
                        const [min, max] = [numberRange().min(), numberRange().max()];
                        if (min === null) return `< ${max}`;
                        if (max === null || max < min) return `> ${min}`;
                        return `${min} - ${max}`;
                    }),
                    filter: (item) => {
                        const [min, max] = [numberRange().min(), numberRange().max()];
                        const value = transform?.(item);
                        if (isNaN(value)) return false;
                        if (min === null) {
                            if (max === null) return true;
                            return value <= max;
                        } else {
                            if (max === null || max < min) return value >= min;
                            return value >= min && value <= max;
                        }
                    },
                };
            case "contact":
                const contactValue = signal<DropDownItem | undefined>(undefined);
                return {
                    id,
                    keepRow,
                    label,
                    type,
                    value: contactValue,
                    active: computed(() => !!contactValue()),
                    clear: () => contactValue.set(undefined),
                    text: computed(() => contactValue()?.itemName ?? ""),
                    filter: (item) => {
                        return transform?.(item) === contactValue()?.itemId;
                    },
                };
            case "dropdownMultiselect":
                const multiselectValue = signal<DropDownItem[] | undefined>(undefined);
                const multiselectIds = computed(() => new Set(multiselectValue()?.map((item) => String(item.itemId))));
                return {
                    id,
                    keepRow,
                    label,
                    type,
                    value: multiselectValue,
                    active: computed(() => !!multiselectValue()?.length),
                    clear: () => multiselectValue.set(undefined),
                    text: computed(
                        () =>
                            multiselectValue()
                                ?.map((item) => item.itemName)
                                .join(", ") ?? "",
                    ),
                    filter: (item) => {
                        return transform?.(item).some((id: number | string) => multiselectIds().has(String(id)));
                    },
                    data,
                };
            // Only includes item if a selected option is true
            case "options": {
                const value = signal<DropDownItem[]>([]);
                return {
                    id,
                    keepRow,
                    label,
                    type,
                    value: value,
                    active: computed(() => true),
                    showText: computed(() => !!value().length),
                    text: computed(() =>
                        value()
                            .map((i) => i.itemName)
                            .join(", "),
                    ),
                    clear: () => value.set([]),
                    filter: (item) => {
                        const data = transform?.(item);
                        if (data.skip) return true;
                        return value().some((val) => !!val.itemId && data[val.itemId] === true);
                    },
                    data,
                };
            }
            case "yesNo":
                const yesNoValue = signal<boolean | undefined>(undefined);
                return {
                    id,
                    keepRow,
                    label,
                    type,
                    value: yesNoValue,
                    active: computed(() => yesNoValue() !== undefined),
                    clear: () => yesNoValue.set(undefined),
                    text: computed(() => (yesNoValue() ? "Yes" : "No")),
                    filter: (item) => transform?.(item) === yesNoValue(),
                };
            default:
                S25Assert.never(type, "Unknown filter type");
        }
    }

    export function hasItemChanged(item: Item): boolean {
        const candidate = item.academicGrid.effective.position;
        return !positionsEqual(
            {
                dow: candidate.dow(),
                startHour: candidate.startHour(),
                endHour: candidate.endHour(),
                locations: candidate.locations(),
                instructors: candidate.instructors(),
            },
            item.academicGrid.current().position,
        );
    }

    export function positionsEqual(left: Position, right: Position): boolean {
        if (!timeEqual(left, right)) return false;
        if (S25Util.array.hasSymmetricDifference(left.locations, right.locations)) return false;
        return !S25Util.array.hasSymmetricDifference(left.instructors, right.instructors);
    }

    function timeEqual(
        left: Pick<Position, "dow" | "startHour" | "endHour">,
        right: Pick<Position, "dow" | "startHour" | "endHour">,
    ): boolean {
        if (left.dow !== right.dow) return false;
        if (left.startHour !== right.startHour) return false;
        if (left.endHour !== right.endHour) return false;
        return true;
    }

    export function getOccurrenceDatesWithAdditionalTime(
        item: Item,
        occ: GridReservation,
    ): {
        start: ISODateString;
        end: ISODateString;
    } {
        const pre = (item.academicGrid.setup + item.academicGrid.pre) / 60;
        const post = (item.academicGrid.post + item.academicGrid.takedown) / 60;
        const start = S25Util.date.addHoursToS25ISODateTimeStr(occ.start, -pre);
        const end = S25Util.date.addHoursToS25ISODateTimeStr(occ.end, post);
        return { start, end };
    }

    export function getTimeString(item: Item, is24Hours: boolean): string {
        const startHour = item.academicGrid.effective.position.startHour();
        const endHour = item.academicGrid.effective.position.endHour();
        const startTime = S25Util.date.toTimeStrFromHours(startHour, is24Hours);
        const endTime = S25Util.date.toTimeStrFromHours(endHour, is24Hours);
        return `${startTime} - ${endTime}`;
    }

    function dowSortIndex(dow: string): number {
        const index = BOARD_CONST.allDows.indexOf(dow);
        return index === -1 ? Number.POSITIVE_INFINITY : index;
    }

    export function compareIssues(left: Issue, right: Issue): number {
        const leftPosition = left.item.academicGrid.effective.position;
        const rightPosition = right.item.academicGrid.effective.position;
        const leftDow = leftPosition.dow();
        const rightDow = rightPosition.dow();
        const dowOrder = dowSortIndex(leftDow) - dowSortIndex(rightDow) || leftDow.localeCompare(rightDow);
        if (dowOrder) return dowOrder;
        const startOrder = leftPosition.startHour() - rightPosition.startHour();
        if (startOrder) return startOrder;
        const endOrder = leftPosition.endHour() - rightPosition.endHour();
        if (endOrder) return endOrder;
        return left.item.academicGrid.eventName.localeCompare(right.item.academicGrid.eventName);
    }

    export function sortIssues(issues: Issue[]): Issue[] {
        return issues.sort(compareIssues);
    }

    export function ratioToHeadCount(itemData: ItemData, ratio: WarningHeadCountRatio): number {
        const headCountKey = ratio === "RHC:CAP" ? "registeredHeadCount" : "expectedHeadCount";
        return itemData[headCountKey] ?? 0;
    }

    export function ratioToHeadCountLabel(ratio: WarningHeadCountRatio): string {
        return ratio === "RHC:CAP" ? "Registered" : "Expected";
    }

    export function getItemPayload(item: Item, occIdGenerator: () => number): EventI {
        const original = item.academicGrid.original();
        const candidate = readCandidate(item.academicGrid.effective);
        const alsoAssignedLocations = new Set(item.academicGrid.candidateAlsoAssign());
        const firstOcc = candidate.occurrences[0];
        return {
            event_id: item.academicGrid.eventId,
            profile: [
                {
                    profile_id: item.academicGrid.profileId,
                    ext_uid: item.academicGrid.profileAlienUid,
                    profile_name: candidate.profileName,
                    profile_code: candidate.profileCode,
                    init_start_dt: firstOcc?.start,
                    init_end_dt: firstOcc?.end,
                    reservation: candidate.occurrences.map((occ, occIndex) => {
                        const isNew = !isDefined(original.occurrences[occIndex]?.id);
                        occ.id = original.occurrences[occIndex]?.id ?? occIdGenerator();
                        const rsrvDates = getOccurrenceDatesWithAdditionalTime(item, occ);
                        return {
                            reservation_id: occ.id,
                            reservation_state: isNew ? Event.Reservation.States.Active : undefined,
                            event_start_dt: occ.start,
                            event_end_dt: occ.end,
                            reservation_start_dt: rsrvDates.start,
                            reservation_end_dt: rsrvDates.end,
                            space_reservation: occ.locations
                                // Also-assigned rooms are expanded/persisted by the server
                                .filter((loc) => !alsoAssignedLocations.has(loc.id))
                                .map((loc) => ({
                                    space_id: loc.id,
                                    layout_id: loc.layoutId,
                                    share: loc.shared ? "T" : "F",
                                    attendance: loc.attendance,
                                })),
                            resource_reservation: occ.resources.map((res) => ({
                                resource_id: res.id,
                                quantity: res.quantity ?? 1,
                            })),
                        };
                    }),
                },
            ],
        };
    }

    export function updateOriginalsWithCandidates(items: readonly Item[]) {
        for (const item of items) {
            item.academicGrid.original.set(readCandidate(item.academicGrid.effective));
        }
    }

    export function commitCandidate(item: Item): void {
        item.academicGrid.current.set(readCandidate(item.academicGrid.effective));
    }

    export function getNewProfileName(
        current: string,
        dow: string,
        startDate: ISODateString,
        startTime: ISOTimeString,
        endTime: ISOTimeString,
    ): string {
        if (!current) return current;
        const re = /(?<pattern>\w+)\s+(?<start>\d\d\d\d)-(?<end>\d\d\d\d)(?:\s+(?<type>\S+))?\s+(?<date>\d\d\/\d\d)/;
        const match = current.match(re);
        if (!match) return current;
        const start = startTime.slice(0, 5).replace(":", "");
        const end = endTime.slice(0, 5).replace(":", "");
        const type = match.groups!.type;
        const date = startDate.slice(5).replace("-", "/");

        if (type) return current.replace(re, `${dow} ${start}-${end} ${type} ${date}`);
        return current.replace(re, `${dow} ${start}-${end} ${date}`);
    }

    export function queryToDropdown(query: GridListItemQuery | undefined): DropDownItem<number> | null {
        return query ? { itemId: query.queryId, itemName: query.queryName } : null;
    }

    export function doOccurrencesOverlap(a: GridReservation[], b: GridReservation[]) {
        return MPGUtil.doOccurrencesOverlap(mapOccurrenceDates(a), mapOccurrenceDates(b));
    }

    export function buildInstructorTimeIndex(items: readonly Item[]): InstructorTimeIndex {
        const index: InstructorTimeIndex = new Map();
        for (const item of items) {
            if (item.academicGrid.isLocationConflict) continue;
            const position = item.academicGrid.current().position;
            if (!position.instructors.length) continue;
            for (const instructorId of position.instructors) {
                if (!index.has(instructorId)) index.set(instructorId, new Map());
                const byDow = index.get(instructorId)!;
                for (const dowChar of position.dow) {
                    if (!byDow.has(dowChar)) byDow.set(dowChar, []);
                    byDow.get(dowChar)!.push(item);
                }
            }
        }
        for (const byDow of index.values()) {
            for (const bucket of byDow.values()) {
                bucket.sort(
                    (left, right) =>
                        left.academicGrid.current().position.startHour -
                        right.academicGrid.current().position.startHour,
                );
            }
        }
        return index;
    }

    export function getOverlappingInstructors(index: InstructorTimeIndex, item: Item): Item[] {
        const position = item.academicGrid.effective.position;
        const instructorIds = position.instructors();
        if (!instructorIds.length) return [];

        const queryStart = position.startHour();
        const queryEnd = position.endHour();
        const hits = new Set<Item>();
        for (const instructorId of instructorIds) {
            const byDow = index.get(instructorId);
            if (!byDow) continue;
            for (const dowChar of position.dow()) {
                const bucket = byDow.get(dowChar);
                if (!bucket) continue;
                for (const other of bucket) {
                    if (other.id === item.id) continue;
                    const otherPosition = other.academicGrid.current().position;
                    if (otherPosition.startHour >= queryEnd) break;
                    if (otherPosition.endHour <= queryStart) continue;
                    hits.add(other);
                }
            }
        }
        return [...hits];
    }

    export function getInstructorConflicts(item: Item, overlaps: readonly Item[], is24Hours = false): Issue[] {
        const conflictItems: Item[] = [];
        for (const other of overlaps) {
            if (!canItemsConflict(item, other)) continue;
            conflictItems.push(other);
        }
        if (!conflictItems.length) return [];
        return [
            {
                item,
                items: [item, ...conflictItems],
                time: getTimeString(item, is24Hours),
            },
        ];
    }

    export function canItemsConflict(a: Item, b: Item) {
        const aCandidate = readCandidate(a.academicGrid.effective);
        if (a.id === b.id) return false;
        if (a.academicGrid.boundProfiles.includes(b.academicGrid.profileId)) return false;
        if (b.academicGrid.boundProfiles.includes(a.academicGrid.profileId)) return false;
        const bCandidate = readCandidate(b.academicGrid.effective);
        if (aCandidate.startDate >= bCandidate.endDate || aCandidate.endDate <= bCandidate.startDate) return false;
        if (!doOccurrencesOverlap(aCandidate.occurrences, bCandidate.occurrences)) return false;
        return true;
    }

    export function getRowKeyFromPickupContext(mode: Mode, context: DowGrid.PickupContext<Item, ProjectionData>) {
        switch (mode) {
            case "schedule":
                return String(context.grabbedEntry.source.academicGrid.profileId);
            case "instructor":
                return String(context.grabbedEntry.data.instructorId);
            case "location":
                return String(context.grabbedEntry.data.locationId);
            default:
                S25Assert.never(mode, "uncovered mode");
        }
    }

    export const invertLocationRelationship = {
        blocks: "blockedBy",
        blockedBy: "blocks",
        dividesInto: "subdivisionOf",
        subdivisionOf: "dividesInto",
        alsoAssign: "alsoAssignedBy",
        alsoAssignedBy: "alsoAssign",
    } as const;

    // Extrapolates the grid relationships to include non-grid locations if they have relationships with grid locations
    export function getFullLocationRelationships(
        locationData: Record<number, GridSpace>,
    ): Map<number, GridSpace["relationships"]> {
        const gridLocations = new Set(Object.values(locationData).map((location) => location.id));
        const fullRelationships = new Map<number, GridSpace["relationships"]>();
        for (const location of Object.values(locationData)) {
            fullRelationships.set(location.id, location.relationships);
            for (const key of S25Util.objectKeys(location.relationships)) {
                for (const id of location.relationships[key]) {
                    if (gridLocations.has(id)) continue;
                    if (!fullRelationships.has(id)) fullRelationships.set(id, emptyRelationships());
                    fullRelationships.get(id)![invertLocationRelationship[key]].push(location.id);
                }
            }
        }

        return fullRelationships;
    }

    export function emptyRelationships(): GridSpace["relationships"] {
        return {
            blocks: [],
            blockedBy: [],
            dividesInto: [],
            subdivisionOf: [],
            alsoAssign: [],
            alsoAssignedBy: [],
        };
    }

    export function getConflictLocations(
        relationshipMap: Map<number, GridSpace["relationships"]>,
        locationIds: number[],
    ) {
        const relationships = locationIds.filter((id) => relationshipMap.has(id)).map((id) => relationshipMap.get(id)!);

        const locationIdsSet = new Set(locationIds);
        const blockingLocations = relationships.flatMap((relationships) => {
            const { dividesInto, subdivisionOf, blocks } = relationships;
            return [dividesInto, subdivisionOf, blocks].flat();
        });
        const alsoAssignedBy = relationships.flatMap((relationship) => relationship.alsoAssignedBy);

        // You cannot also-assign a location that is blocked, so if any of the blocked location
        // are "also assign"ed by other locations, those locations need conflicts as well
        const blockedAlsoAssignedBy = blockingLocations
            .filter((id) => relationshipMap.has(id))
            .flatMap((id) => relationshipMap.get(id)!.alsoAssignedBy);

        return S25Util.array
            .unique([blockingLocations, alsoAssignedBy, blockedAlsoAssignedBy].flat())
            .filter((id) => !locationIdsSet.has(id));
    }

    export function getAlsoAssignIssues(items: readonly Item[], is24Hours: boolean): Issues {
        const issues = items
            .map((item): Issue => {
                const alsoAssign = item.academicGrid.candidateAlsoAssign();
                return { item, time: getTimeString(item, is24Hours), locations: alsoAssign };
            })
            .filter((also) => !!also.locations!.length);

        const issueType: IssueType = {
            id: "alsoAssigns",
            label: "Also Assigned Locations",
            action: "warn",
            issues,
            showCount: false,
        };
        return { counts: getIssueCounts([issueType]), types: [issueType] };
    }

    export function getIssueCounts(types: AcademicGrid.IssueType[]): AcademicGrid.Issues["counts"] {
        types = types.filter((type) => type.issues.length > 0);
        const typesByAction = S25Util.array.groupBy(types, (type) => type.action);
        const countsByAction = <Partial<Record<AcademicGrid.WarningLevel, number>>>(
            Object.fromEntries(
                Object.entries(typesByAction).map(([action, types]) => [
                    action,
                    S25Util.array.count(types, (type) => type.issues.length),
                ]),
            )
        );
        return {
            types: types.length,
            total: S25Util.array.count(types, (type) => type.issues.length),
            warningLevels: Object.keys(typesByAction).length,
            ignore: countsByAction["ignore"] ?? 0,
            info: countsByAction["info"] ?? 0,
            warn: countsByAction["warn"] ?? 0,
            prevent: countsByAction["prevent"] ?? 0,
        };
    }

    export function getOrphanedAlsoAssignIssues(
        items: Item[],
        relationships: Map<number, GridSpace["relationships"]>,
        is24Hours: boolean,
    ): Issues {
        const issues = items
            .map((item): Issue => {
                const orphans = getOrphanedAlsoAssignees(item, relationships);
                return { item, time: getTimeString(item, is24Hours), locations: orphans };
            })
            .filter((also) => !!also.locations!.length);

        const issueType: IssueType = {
            id: "orphanedAlsoAssigns",
            label: "Orphaned Also Assigned Locations",
            action: "warn",
            issues,
            showCount: false,
        };
        return { counts: getIssueCounts([issueType]), types: [issueType] };
    }

    // Get list of assigned locations which are "also assign"ed by the location which was just removed
    // and not "also assign"ed by any remaining or the new location
    function getOrphanedAlsoAssignees(item: Item, relationships: Map<number, GridSpace["relationships"]>): number[] {
        const change = item.academicGrid.changedLocation();
        if (!change || change.old === change.new || change.old === "none") return [];
        const current = item.academicGrid.current().position.locations;

        const newLocations = new Set(current.map((loc) => (loc === change.old ? change.new : loc)));
        return current.filter((loc) => {
            if (loc === change.old) return false;

            const assignedBy = relationships.get(loc)?.alsoAssignedBy ?? [];
            const active = assignedBy.some((by) => newLocations.has(by));
            return !active && assignedBy.includes(<number>change.old);
        });
    }

    export import availability = AcademicGridAvailability;
}
